import { type FormEvent, memo, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { SCHOOLS } from '../Schools/tokens';
import { startImpersonation } from '../../core/impersonation';
import {
  fetchImpersonationCandidates,
  lookupImpersonationTarget,
  ROLE_OPTIONS,
  roleLabel,
  type ImpersonationCandidate,
  type ImpersonationCandidateList,
  type ImpersonationLookup,
  type ImpersonationSearch,
} from './impersonationApi';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

type CandidateSource = (search: ImpersonationSearch) => Promise<ImpersonationCandidateList>;

type Props = {
  source?: CandidateSource;
  lookup?: (email: string) => Promise<ImpersonationLookup>;
  debounceMs?: number;
};

const EVERYONE: ImpersonationSearch = { query: '', role: '' };

const bodyText = {
  fontFamily: SCHOOLS.font,
  fontSize: 16,
  lineHeight: '24px',
  color: SCHOOLS.muted,
} as const;

const cellSx = {
  fontFamily: SCHOOLS.font,
  fontSize: 14,
  color: SCHOOLS.ink,
  borderBottom: `1px solid ${SCHOOLS.border}`,
} as const;

const buttonSx = {
  textTransform: 'none',
  fontFamily: SCHOOLS.font,
  fontWeight: 600,
} as const;

type ListState =
  | { kind: 'loading' }
  | { kind: 'list'; rows: ImpersonationCandidate[]; truncated: boolean }
  | { kind: 'lookup'; failed: boolean };

export function AdminImpersonationPage({
  source = fetchImpersonationCandidates,
  lookup = lookupImpersonationTarget,
  debounceMs = 250,
}: Props = {}) {
  const [state, setState] = useState<ListState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    source(EVERYONE)
      .then((result) => {
        if (cancelled) return;
        setState(
          result.available
            ? {
                kind: 'list',
                rows: result.users,
                truncated: result.truncated === true,
              }
            : { kind: 'lookup', failed: false },
        );
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'lookup', failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  return (
    <Box
      sx={{
        display: 'flex',
        bgcolor: SCHOOLS.surface,
        minHeight: HEIGHT_BELOW_BARS,
      }}
    >
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box component="main" sx={{ flex: 1, minWidth: 0, p: '24px 32px' }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: SCHOOLS.font,
            fontSize: 24,
            fontWeight: 700,
            color: SCHOOLS.ink,
          }}
        >
          Impersonate a user
        </Typography>
        <Typography sx={{ ...bodyText, fontSize: 14, lineHeight: '20px', mb: 2 }}>
          See the app exactly as a district admin sees it. Every action you take while impersonating
          is recorded against their account with a note that you performed it.
        </Typography>
        {state.kind === 'loading' && (
          <CircularProgress data-testid="impersonation-loading" size={24} />
        )}
        {state.kind === 'list' && (
          <CandidateList
            initialRows={state.rows}
            initialTruncated={state.truncated}
            source={source}
            debounceMs={debounceMs}
          />
        )}
        {state.kind === 'lookup' && (
          <>
            {state.failed && (
              <Typography data-testid="impersonation-error" sx={{ ...bodyText, mb: 2 }}>
                Could not load the user list. Look a user up by email instead.
              </Typography>
            )}
            <EmailLookup lookup={lookup} />
          </>
        )}
      </Box>
    </Box>
  );
}

type CandidateListProps = {
  initialRows: ImpersonationCandidate[];
  initialTruncated: boolean;
  source: CandidateSource;
  debounceMs: number;
};

function countLine(rows: ImpersonationCandidate[], truncated: boolean, filtering: boolean): string {
  if (truncated)
    return `Showing the first ${rows.length} matching users. Keep typing to narrow the list.`;
  if (filtering) return `${rows.length} ${rows.length === 1 ? 'match' : 'matches'}`;
  return `${rows.length} ${rows.length === 1 ? 'user' : 'users'}`;
}

function CandidateList({ initialRows, initialTruncated, source, debounceMs }: CandidateListProps) {
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const [rows, setRows] = useState(initialRows);
  const [truncated, setTruncated] = useState(initialTruncated);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);
  const latest = useRef(0);
  const filtering = Boolean(query.trim() || role);

  useEffect(() => {
    if (latest.current === 0 && !query && !role) return;
    const ticket = ++latest.current;
    setSearching(true);
    const timer = setTimeout(() => {
      source({ query: query.trim(), role })
        .then((result) => {
          if (ticket !== latest.current) return;
          setRows(result.users);
          setTruncated(result.truncated === true);
          setFailed(false);
        })
        .catch(() => {
          if (ticket === latest.current) setFailed(true);
        })
        .finally(() => {
          if (ticket === latest.current) setSearching(false);
        });
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [query, role, source, debounceMs]);

  if (rows.length === 0 && !filtering && !failed) {
    return (
      <Typography data-testid="impersonation-empty" sx={bodyText}>
        No user can be impersonated yet. Grant access to a district admin first.
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, maxWidth: 680 }}>
        <TextField
          label="Search"
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Email, name, or district"
          fullWidth
        />
        <TextField
          select
          label="Role"
          size="small"
          value={role}
          onChange={(event) => setRole(event.target.value)}
          slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
          sx={{ minWidth: 160 }}
        >
          <option value="">All roles</option>
          {ROLE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {roleLabel(option)}
            </option>
          ))}
        </TextField>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography
          data-testid="impersonation-count"
          sx={{ ...bodyText, fontSize: 13, lineHeight: '18px' }}
        >
          {countLine(rows, truncated, filtering)}
        </Typography>
        {searching && <CircularProgress data-testid="impersonation-searching" size={14} />}
      </Box>
      {failed && (
        <Typography data-testid="impersonation-search-error" sx={{ ...bodyText, mb: 1.5 }}>
          The search failed. Try again in a moment.
        </Typography>
      )}
      {rows.length === 0 ? (
        <Typography data-testid="impersonation-no-match" sx={bodyText}>
          No user matches that search.
        </Typography>
      ) : (
        <Table
          size="small"
          sx={{
            bgcolor: SCHOOLS.cardBg,
            borderRadius: '4px',
            boxShadow: SCHOOLS.cardShadow,
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }}>District</TableCell>
              <TableCell sx={cellSx} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <CandidateRow key={row.sub} row={row} />
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}

const CandidateRow = memo(function CandidateRow({ row }: { row: ImpersonationCandidate }) {
  return (
    <TableRow data-testid="impersonation-row" data-email={row.email}>
      <TableCell sx={cellSx}>{row.email}</TableCell>
      <TableCell sx={cellSx}>{row.name}</TableCell>
      <TableCell sx={cellSx} data-testid="impersonation-role">
        {roleLabel(row.role)}
      </TableCell>
      <TableCell sx={cellSx}>{row.districtName ?? ''}</TableCell>
      <TableCell sx={cellSx} align="right">
        <Button
          size="small"
          variant="contained"
          onClick={() =>
            startImpersonation({
              sub: row.sub,
              email: row.email,
              districtName: row.districtName,
            })
          }
          sx={buttonSx}
        >
          Impersonate
        </Button>
      </TableCell>
    </TableRow>
  );
});

function describeResult(result: ImpersonationLookup): string {
  if (!result.found) return 'No user with that email exists.';
  if (result.eligible) return `${result.email} · ${result.districtName ?? 'district unknown'}`;
  switch (result.reason) {
    case 'super_admin':
      return 'That user is a super admin. Super admins cannot impersonate each other.';
    case 'no_access':
      return 'That user does not have access, so there is nothing to see as them.';
    default:
      return 'That user could not be resolved. They may not be set up in Flashlight yet.';
  }
}

function EmailLookup({ lookup }: { lookup: (email: string) => Promise<ImpersonationLookup> }) {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<ImpersonationLookup | null>(null);
  const [failed, setFailed] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || pending) return;
    setPending(true);
    setFailed(false);
    setResult(null);
    lookup(trimmed)
      .then((next) => setResult(next))
      .catch(() => setFailed(true))
      .finally(() => setPending(false));
  };

  const impersonate = () => {
    if (!result?.eligible || !result.sub || !result.email) return;
    startImpersonation({
      sub: result.sub,
      email: result.email,
      districtName: result.districtName,
    });
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={submit}
        sx={{ display: 'flex', gap: 1.5, alignItems: 'center', maxWidth: 560 }}
      >
        <TextField
          label="Email"
          size="small"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={pending}
          fullWidth
        />
        <Button type="submit" variant="contained" disabled={pending} sx={buttonSx}>
          Look up
        </Button>
      </Box>
      {pending && (
        <CircularProgress data-testid="impersonation-lookup-loading" size={24} sx={{ mt: 2 }} />
      )}
      {failed && (
        <Typography data-testid="impersonation-lookup-error" sx={{ ...bodyText, mt: 2 }}>
          The lookup failed. Try again in a moment.
        </Typography>
      )}
      {result && (
        <Box
          data-testid="impersonation-result"
          sx={{
            mt: 2,
            p: 2,
            maxWidth: 560,
            bgcolor: SCHOOLS.cardBg,
            borderRadius: '4px',
            boxShadow: SCHOOLS.cardShadow,
          }}
        >
          <Typography sx={{ ...bodyText, color: SCHOOLS.ink }}>{describeResult(result)}</Typography>
          {result.eligible && (
            <Button variant="contained" onClick={impersonate} sx={{ ...buttonSx, mt: 1.5 }}>
              Impersonate
            </Button>
          )}
        </Box>
      )}
    </>
  );
}
